import React from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Rating, Box, Typography, CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import api from "../api/axios";
import { useAuth } from "../auth/useAuth";
import { useAppSnackbar } from "./AppSnackbarProvider";

export default function FeedbackDialog({ open, onClose, context = "general", contextRef = null }) {
  const { t } = useTranslation();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { showSnackbar } = useAppSnackbar();

  const [rating, setRating] = React.useState(null);
  const [message, setMessage] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  const reset = () => {
    setRating(null);
    setMessage("");
    setError("");
  };

  const handleClose = () => {
    if (loading) return;
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    setError("");
    if (!message.trim() && rating === null) {
      setError(t("feedback.validation_required", "Merci d'ajouter une note ou un commentaire."));
      return;
    }
    setLoading(true);
    try {
      await api.post("/feedback", {
        message: message.trim() || null,
        rating: rating,
        context,
        context_ref: contextRef,
        page: location.pathname,
      });
      showSnackbar(t("feedback.success", "Merci pour votre retour !"), "success");
      reset();
      onClose();
    } catch (e) {
      const msg = e?.response?.data?.detail || t("feedback.error", "Impossible d'envoyer le feedback.");
      setError(typeof msg === "string" ? msg : t("feedback.error", "Impossible d'envoyer le feedback."));
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("feedback.title", "Votre avis compte")}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {t("feedback.rating_label", "Note (optionnel)")}
            </Typography>
            <Rating
              value={rating}
              onChange={(_, v) => setRating(v)}
              size="large"
            />
          </Box>
          <TextField
            label={t("feedback.message_label", "Votre commentaire")}
            multiline
            minRows={3}
            maxRows={6}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={loading}
            fullWidth
          />
          {error && (
            <Typography variant="caption" color="error">{error}</Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          {t("feedback.cancel", "Annuler")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {t("feedback.submit", "Envoyer")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
